import { Alert, Button, Cell, FormControl, Grid, HFlow, Tooltip } from 'bold-ui'
import {
  CheckboxField,
  CidadaoSelectField,
  CnsField,
  CpfField,
  DateField,
  ErrorField,
  SexoSelectField,
  SwitchField,
  TextField,
} from 'components/form'
import { JustificativaNaoPossuiCpfSelectField } from 'components/form/field/select/JustificativaNaoPossuiCpfSelectField'
import { css } from 'emotion'
import { FormApi } from 'final-form'
import { useVerificaCidadaoCadastradoLazyQuery } from 'graphql/hooks.generated'
import { TipoAtividadeEnum } from 'graphql/types.generated'
import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { EditableListForm, EditableListFormRenderProps } from 'view/atendimentos/detail/components/EditableListForm'
import { ProfissionalCpfCnsIdModel } from 'view/atividade-coletiva/model-atividadeColetiva'

import {
  naoPossuiCpfDecorator,
  PARTICIPANTE_EDITABLE_TABLE_PREFIX,
  participantesDecorator,
} from './calculator-participantesEditableTable'
import { ParticipanteEditableTableModel } from './ParticipanteEditableTable'
import { participanteEditableTableValidator } from './validator-participantesEditableTable'

interface ParticipanteEditableTableProps {
  now: Date
  required: boolean
  allItems: ParticipanteEditableTableModel[]
  profissionalResponsavel: ProfissionalCpfCnsIdModel
  profissionaisEnvolvidos: ProfissionalCpfCnsIdModel[]
  dataAtividade: LocalDate
  tipoAtividade: TipoAtividadeEnum
  onSubmit: (formValues: ParticipanteEditableTableModel, formApi: FormApi) => Promise<number>
}

export const ParticipanteEditableTableForm = (props: ParticipanteEditableTableProps) => {
  const {
    now,
    required,
    allItems,
    profissionalResponsavel,
    profissionaisEnvolvidos,
    dataAtividade,
    tipoAtividade,
    onSubmit,
  } = props

  const formRef = useRef<FormApi>()
  const valuesRef = useRef<ParticipanteEditableTableModel>()
  const hasSubmittedRef = useRef(false)
  const [
    verificaCidadaoCadastradoQuery,
    { data: verificaCidadaoCadastradoQueryResult },
  ] = useVerificaCidadaoCadastradoLazyQuery({
    fetchPolicy: 'network-only',
  })
  const [errorCidadaoJaCadastrado, setErrorCidadaoJaCadastrado] = useState<string>()

  const handleOnSubmit = useCallback(
    (values: ParticipanteEditableTableModel, form: FormApi) => {
      if (values.isCidadaoParticipanteFormOpen) {
        const cpf = values.cidadaoParticipante?.cpf
        const cns = values.cidadaoParticipante?.cns

        if (cpf || cns) {
          formRef.current = form
          valuesRef.current = values
          hasSubmittedRef.current = false
          verificaCidadaoCadastradoQuery({ variables: { cpf, cns } })
          return
        }
      }
      return onSubmit(values, form)
    },
    [verificaCidadaoCadastradoQuery, onSubmit]
  )

  useEffect(() => {
    if (!verificaCidadaoCadastradoQueryResult || !formRef.current) return

    const { cpfJaCadastrado, cnsJaCadastrado } = verificaCidadaoCadastradoQueryResult

    if (cpfJaCadastrado || cnsJaCadastrado) {
      setErrorCidadaoJaCadastrado('Cidadão possui cadastro.')
      return
    }

    setErrorCidadaoJaCadastrado(undefined)

    if (!hasSubmittedRef.current && valuesRef.current) {
      hasSubmittedRef.current = true
      onSubmit(valuesRef.current, formRef.current)
    }
  }, [verificaCidadaoCadastradoQueryResult, onSubmit])

  const renderForm = (renderProps: EditableListFormRenderProps<ParticipanteEditableTableModel>) => {
    const { name, handleSubmit, values } = renderProps
    const mustDisableSubmitButton = !values?.cidadao && !values?.isCidadaoParticipanteFormOpen

    const currentCpf = values?.cidadaoParticipante?.cpf
    const currentCns = values?.cidadaoParticipante?.cns

    if (!currentCpf && !currentCns && errorCidadaoJaCadastrado) {
      setErrorCidadaoJaCadastrado(undefined)
    }

    const stNaoPossuiCpf = values?.cidadaoParticipante?.stNaoPossuiCpf

    return (
      <Grid gap={0.5} style={styles.tableHeader}>
        <Cell lg={4} md={3}>
          <CidadaoSelectField
            name={name.cidadao}
            label='Cidadão'
            placeholder='Pesquise por nome, CPF ou CNS'
            required={required}
            disabled={values?.isCidadaoParticipanteFormOpen}
            selectedCidadaoIds={allItems?.length ? allItems.map((item) => item.cidadao?.id).filterNotFalsy() : []}
          />
        </Cell>

        <Cell size={12}>
          <SwitchField
            label='Adicionar cidadão sem cadastro na base local'
            initialValue={false}
            name={name.isCidadaoParticipanteFormOpen}
          />
        </Cell>

        {values?.isCidadaoParticipanteFormOpen && (
          <Fragment>
            <Cell size={3}>
              <TextField name={name.cidadaoParticipante.nome} label='Nome do cidadão' maxLength={70} uppercase />
            </Cell>

            <Cell size={2}>
              <Tooltip text={stNaoPossuiCpf ? 'Campo desabilitado porque o cidadão não possui CPF' : ''}>
                <CpfField label='CPF' name={name.cidadaoParticipante.cpf} disabled={stNaoPossuiCpf} />
              </Tooltip>
            </Cell>

            <Cell size={2}>
              <FormControl label={<span>&nbsp;</span>}>
                <CheckboxField name={name.cidadaoParticipante.stNaoPossuiCpf} label='Não possui CPF' />
              </FormControl>
            </Cell>

            <Cell size={2}>
              <DateField
                name={name.cidadaoParticipante.dataNascimento}
                maxDate={now}
                label='Data de nascimento'
                required
              />
            </Cell>

            <Cell size={2}>
              <SexoSelectField label='Sexo' name={name.cidadaoParticipante.sexo} required />
            </Cell>

            {stNaoPossuiCpf && (
              <>
                <Cell size={3}>
                  <CnsField label='CNS' name={name.cidadaoParticipante.cns} />
                </Cell>

                <Cell size={4}>
                  <JustificativaNaoPossuiCpfSelectField
                    name={name.cidadaoParticipante.justificativaNaoPossuiCpf}
                    label='Justificativa'
                    required={stNaoPossuiCpf}
                  />
                </Cell>
              </>
            )}

            <Cell size={7}>
              <ErrorField type='alert' name={`${name.cidadaoParticipante.absolutePath()}._participanteError`} />
            </Cell>

            <Cell size={12}>
              {errorCidadaoJaCadastrado && (
                <Cell size={3}>
                  <Alert type='danger'>{errorCidadaoJaCadastrado}</Alert>
                </Cell>
              )}

              <Cell size={7}>
                <Alert type='warning'>
                  O registro é feito apenas na atividade coletiva, os campos não substituem o cadastro do cidadão.
                </Alert>
              </Cell>
            </Cell>
          </Fragment>
        )}

        <Cell size={12}>
          <HFlow justifyContent='flex-end'>
            <Tooltip text={mustDisableSubmitButton ? 'Informe um cidadão para adicionar à lista' : ''}>
              <Button kind='primary' disabled={mustDisableSubmitButton} onClick={handleSubmit} size='small'>
                Adicionar
              </Button>
            </Tooltip>
          </HFlow>
        </Cell>
      </Grid>
    )
  }

  return (
    <EditableListForm<ParticipanteEditableTableModel>
      render={renderForm}
      onSubmit={handleOnSubmit}
      validate={participanteEditableTableValidator(
        tipoAtividade,
        dataAtividade,
        allItems,
        profissionalResponsavel,
        profissionaisEnvolvidos
      )}
      decorators={[participantesDecorator, naoPossuiCpfDecorator]}
      prefix={PARTICIPANTE_EDITABLE_TABLE_PREFIX}
    />
  )
}

const styles = {
  tableHeader: css`
    padding-block: 0.5rem;
  `,
}
